import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { keys } from "@/data/keys";
import type { EntityType, ProposalStatus, ProposalField } from "@/config";

export type ProposalInsert = {
  entity_type: EntityType;
  entity_id: string;
  proposed_description: string;
  proposed_by: string;
  owner_id: string;
  field_type: ProposalField | string;
};

function invalidateProposalScopes(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: keys.proposals.all });
  qc.invalidateQueries({ queryKey: keys.notifications.all });
}

export function useCreateProposal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: ProposalInsert) => {
      const { data, error } = await supabase
        .from("description_proposals")
        .insert(input)
        .select("id, proposed_description, created_at")
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => invalidateProposalScopes(qc),
  });
}

export function useWithdrawProposal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id }: { id: string }) => {
      const { error } = await supabase.from("description_proposals").delete().eq("id", id);
      if (error) throw error;
      return { id };
    },
    onSuccess: () => invalidateProposalScopes(qc),
  });
}

export function useUpdateProposalStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, status }: { id: string; status: Extract<ProposalStatus, "accepted" | "rejected"> }) => {
      const { error } = await supabase
        .from("description_proposals")
        .update({ status, updated_at: new Date().toISOString() })
        .eq("id", id);
      if (error) throw error;
      return { id, status };
    },
    onSuccess: () => {
      invalidateProposalScopes(qc);
      // Accepting a proposal updates burger/producer rows — invalidate broadly.
      qc.invalidateQueries({ queryKey: keys.burgers.all });
      qc.invalidateQueries({ queryKey: keys.producers.all });
    },
  });
}
